import React, { useEffect, useState } from 'react';

import { resolveBookMediaUrl } from './bookMediaUrl';

const selectedProposal = (image, language) => {
  if (!Array.isArray(image?.proposals)) return null;
  const activeId = image.active_proposal_ids?.[language];
  return (
    image.proposals.find((proposal) => proposal?.id === activeId) ||
    image.proposals[0] ||
    null
  );
};

const imageSource = (image, language) =>
  typeof image === 'string'
    ? image
    : selectedProposal(image, language)?.url || image?.url || '';

const imageLabel = (image, index, language) => {
  if (typeof image === 'string') return `Image ${index + 1}`;
  const proposal = selectedProposal(image, language);
  return (
    proposal?.captions?.[language] ||
    image?.caption ||
    image?.title ||
    image?.filename ||
    image?.url ||
    `Image ${index + 1}`
  );
};

const imageKey = (image, index, language) => {
  const source = imageSource(image, language);
  if (typeof image === 'string') return `${index}:${source}`;
  return `${index}:${image?.id || image?.filename || ''}:${source}`;
};

const VisualImageListEditor = ({
  images = [],
  contentLanguage = 'fr',
  onChange,
  onPlaceImage,
}) => {
  const normalizedImages = Array.isArray(images) ? images : [];
  const [failedPreviews, setFailedPreviews] = useState(() => new Set());

  useEffect(() => {
    const currentKeys = new Set(
      normalizedImages.map((image, index) =>
        imageKey(image, index, contentLanguage)
      )
    );
    setFailedPreviews((current) => {
      const next = new Set(
        [...current].filter((key) => currentKeys.has(key))
      );
      if (next.size === current.size) return current;
      return next;
    });
  }, [contentLanguage, normalizedImages]);

  const replaceImage = (imageIndex, nextImage) => {
    onChange?.(
      normalizedImages.map((image, index) =>
        index === imageIndex ? nextImage : image
      )
    );
  };

  const replaceCaption = (imageIndex, image, caption) => {
    if (Array.isArray(image?.proposals)) {
      const proposal = selectedProposal(image, contentLanguage);
      if (!proposal) return;
      replaceImage(imageIndex, {
        ...image,
        proposals: image.proposals.map((candidate) =>
          candidate?.id === proposal.id
            ? {
                ...candidate,
                captions: {
                  fr: '',
                  en: '',
                  pt: '',
                  ...(candidate.captions || {}),
                  [contentLanguage]: caption,
                },
              }
            : candidate
        ),
      });
      return;
    }
    replaceImage(imageIndex, {
      ...(typeof image === 'object' && image !== null
        ? image
        : { url: imageSource(image, contentLanguage) }),
      caption,
    });
  };

  if (normalizedImages.length === 0) return null;

  return (
    <section className="grid min-w-0 gap-3">
      <h4 className="text-sm font-semibold text-gray-800">Images du niveau</h4>
      {normalizedImages.map((image, index) => {
        const source = imageSource(image, contentLanguage);
        const resolvedSource = resolveBookMediaUrl(source);
        const label = imageLabel(image, index, contentLanguage);
        const key = imageKey(image, index, contentLanguage);
        const previewFailed = failedPreviews.has(key);
        const canPlace =
          Boolean(resolvedSource) &&
          !(
            image &&
            typeof image === 'object' &&
            image.placement?.type === 'inline'
          );

        return (
          <article
            key={key}
            role="region"
            aria-label={`Image ${index + 1}`}
            className="grid min-w-0 gap-3 rounded border border-gray-200 bg-white p-3 sm:grid-cols-[10rem_minmax(0,1fr)]"
          >
            <div className="flex min-h-28 items-center justify-center overflow-hidden rounded border border-gray-200 bg-gray-50 p-2 text-center text-sm text-gray-600">
              {resolvedSource && !previewFailed ? (
                <img
                  src={resolvedSource}
                  alt={label}
                  className="max-h-40 max-w-full object-contain"
                  onError={() =>
                    setFailedPreviews((current) => {
                      const next = new Set(current);
                      next.add(key);
                      return next;
                    })
                  }
                />
              ) : (
                <span>
                  {resolvedSource
                    ? 'Image introuvable'
                    : "Entrée d’image invalide"}
                </span>
              )}
            </div>

            <div className="grid min-w-0 content-start gap-2">
              <p className="break-words text-sm font-medium text-gray-800">
                {label}
              </p>
              <label className="grid gap-1 text-sm">
                <span>Légende</span>
                <input
                  type="text"
                  aria-label={`Légende de l’image ${index + 1}`}
                  className="rounded border border-gray-300 px-2 py-1"
                  value={
                    selectedProposal(image, contentLanguage)?.captions?.[
                      contentLanguage
                    ] || (typeof image === 'object' ? image?.caption || '' : '')
                  }
                  onChange={(event) =>
                    replaceCaption(index, image, event.target.value)
                  }
                />
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  aria-label={`Placer l’image ${index + 1} au curseur`}
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:cursor-not-allowed disabled:bg-gray-300"
                  disabled={!canPlace}
                  onClick={() => onPlaceImage?.(index)}
                >
                  Placer au curseur
                </button>
                <button
                  type="button"
                  aria-label={`Supprimer l’image ${index + 1}`}
                  className="rounded bg-red-600 px-3 py-1 text-sm text-white"
                  onClick={() =>
                    onChange?.(
                      normalizedImages.filter(
                        (_, imageIndex) => imageIndex !== index
                      )
                    )
                  }
                >
                  Supprimer
                </button>
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
};

export default VisualImageListEditor;
